// src/features/rewardSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { AppDispatch, RootState } from "../app/store";
import { apiForSSO } from "../utils/utility";
import axios from "axios";
import { fetchReceivedGiftBoxes } from "./receivedBoxesSlice";

/* ---------- Types ---------- */
export interface AssignedRewardBox {
  box: string;
  reward_id: string | null; // null when not assigned; otherwise reward id string
}

export interface RewardUnlocked {
  _id: string;
  created_at: string;
  user_id: string;
  campaign: string;
  reward_name: string;
  reward_id: string;
  threshold: number;
  deposit_delta_usd: number;
  trigger_transaction_id: string;
  delivered: boolean;
  for: string;
  updated_at: string;
  [key: string]: any;
}

export interface DepositDeltaRewardsResponse {
  user_id: string;
  deposit_delta_usd: number;
  rewards_unlocked: RewardUnlocked[];
}

type AsyncStatus = "idle" | "loading" | "succeeded" | "failed";

/* ---------- State ---------- */
interface RewardState {
  assignedRewardBoxes: AssignedRewardBox[];
  fetchAssignedStatus: AsyncStatus;
  fetchAssignedError: string | null;
  isFetchAssignedInProgress: boolean;

  // selection
  selectedRewardBox: AssignedRewardBox | null;

  // assign API
  assignRewardStatus: AsyncStatus;
  assignRewardError: string | null;
  isAssignRewardInProgress: boolean;

  // deposit delta rewards API
  depositDeltaStatus: AsyncStatus;
  depositDeltaError: string | null;
  isDepositDeltaInProgress: boolean;
  depositDeltaData: DepositDeltaRewardsResponse | null;
}

const initialState: RewardState = {
  assignedRewardBoxes: [],
  fetchAssignedStatus: "idle",
  fetchAssignedError: null,
  isFetchAssignedInProgress: false,

  selectedRewardBox: null,

  assignRewardStatus: "idle",
  assignRewardError: null,
  isAssignRewardInProgress: false,

  depositDeltaStatus: "idle",
  depositDeltaError: null,
  isDepositDeltaInProgress: false,
  depositDeltaData: null,
};

/* ---------- Thunks ---------- */
export const fetchAssignedRewardBoxes = createAsyncThunk<
  AssignedRewardBox[],
  void,
  { dispatch: AppDispatch; rejectValue: string }
>("rewards/fetchAssignedRewardBoxes", async (_arg, { rejectWithValue }) => {
  try {
    const res = await apiForSSO.get("/admin/assigned_reward_boxes");
    return res.data as AssignedRewardBox[];
  } catch (error: any) {
    if (error?.response) {
      const status = error.response.status;
      const detail =
        error.response.data?.detail ||
        error.response.data?.message ||
        "Request failed";
      if (status === 400) return rejectWithValue(detail);
      if (status === 401) return rejectWithValue("Unauthorized. Please sign in again.");
      return rejectWithValue(`${status}: ${detail}`);
    }
    return rejectWithValue("Network error. Please try again.");
  }
});

/**
 * Assign a reward to a box.
 * Example:
 *   GET /admin/assign_rewards_box?box_name=Silver%20Box&reward_id=123
 * Success: { detail: "Operation is successful." }
 */
export const assignRewardBox = createAsyncThunk<
  { detail: string },
  { box_name: string; reward_id: string },
  { dispatch: AppDispatch; rejectValue: string }
>("rewards/assignRewardBox", async ({ box_name, reward_id }, { dispatch, rejectWithValue }) => {
  try {
    const res = await apiForSSO.get("/admin/assign_rewards_box", {
      params: { box_name, reward_id },
    });
    // Refresh list after successful assignment
    dispatch(fetchAssignedRewardBoxes());
    return res.data as { detail: string };
  } catch (error: any) {
    if (error?.response) {
      const status = error.response.status;
      const detail =
        error.response.data?.detail ||
        error.response.data?.message ||
        "Request failed";
      if (status === 400) return rejectWithValue(detail);
      if (status === 401) return rejectWithValue("Unauthorized. Please sign in again.");
      return rejectWithValue(`${status}: ${detail}`);
    }
    return rejectWithValue("Network error. Please try again.");
  }
});

/**
 * Get user's deposit delta and unlocked rewards
 * GET https://demo1-api.boxchaos.com/api/gifts/deposit-delta-rewards
 */
export const fetchDepositDeltaRewards = createAsyncThunk<
  DepositDeltaRewardsResponse,
  void,
  { dispatch: AppDispatch; rejectValue: string }
>("rewards/fetchDepositDeltaRewards", async (_arg, { dispatch, rejectWithValue }) => {
  try {
    // Using apiForSSO instance so Authorization interceptor applies; absolute URL overrides baseURL.
    const res = await axios.get(
      "/gifts/deposit-delta-rewards"
    );

    dispatch(fetchReceivedGiftBoxes());
    return res.data as DepositDeltaRewardsResponse;
  } catch (error: any) {
    if (error?.response) {
      const status = error.response.status;
      const detail =
        error.response.data?.detail ||
        error.response.data?.message ||
        "Request failed";
      if (status === 400) return rejectWithValue(detail);
      if (status === 401) return rejectWithValue("Unauthorized. Please sign in again.");
      return rejectWithValue(`${status}: ${detail}`);
    }
    return rejectWithValue("Network error. Please try again.");
  }
});

/* ---------- Slice ---------- */
const rewardSlice = createSlice({
  name: "rewards",
  initialState,
  reducers: {
    resetFetchAssignedStatus(state) {
      state.fetchAssignedStatus = "idle";
      state.fetchAssignedError = null;
      state.isFetchAssignedInProgress = false;
    },
    setAssignedRewardBoxes(state, action: PayloadAction<AssignedRewardBox[]>) {
      state.assignedRewardBoxes = action.payload;
    },
    clearAssignedRewardBoxes(state) {
      state.assignedRewardBoxes = [];
    },
    setSelectedRewardBox(state, action: PayloadAction<AssignedRewardBox | null>) {
      state.selectedRewardBox = action.payload;
    },
    resetAssignRewardStatus(state) {
      state.assignRewardStatus = "idle";
      state.assignRewardError = null;
      state.isAssignRewardInProgress = false;
    },
    resetDepositDeltaStatus(state) {
      state.depositDeltaStatus = "idle";
      state.depositDeltaError = null;
      state.isDepositDeltaInProgress = false;
      // keep depositDeltaData; drop it here if you want a hard reset
      // state.depositDeltaData = null;
    },
  },
  extraReducers: (builder) => {
    // fetchAssignedRewardBoxes
    builder
      .addCase(fetchAssignedRewardBoxes.pending, (state) => {
        state.fetchAssignedStatus = "loading";
        state.fetchAssignedError = null;
        state.isFetchAssignedInProgress = true;
      })
      .addCase(fetchAssignedRewardBoxes.fulfilled, (state, action: PayloadAction<AssignedRewardBox[]>) => {
        state.fetchAssignedStatus = "succeeded";
        state.assignedRewardBoxes = action.payload || [];
        state.isFetchAssignedInProgress = false;
      })
      .addCase(fetchAssignedRewardBoxes.rejected, (state, action) => {
        state.fetchAssignedStatus = "failed";
        state.fetchAssignedError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to fetch assigned reward boxes";
        state.isFetchAssignedInProgress = false;
      });

    // assignRewardBox
    builder
      .addCase(assignRewardBox.pending, (state) => {
        state.assignRewardStatus = "loading";
        state.assignRewardError = null;
        state.isAssignRewardInProgress = true;
      })
      .addCase(assignRewardBox.fulfilled, (state) => {
        state.assignRewardStatus = "succeeded";
        state.isAssignRewardInProgress = false;
      })
      .addCase(assignRewardBox.rejected, (state, action) => {
        state.assignRewardStatus = "failed";
        state.assignRewardError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to assign reward to box";
        state.isAssignRewardInProgress = false;
      });

    // fetchDepositDeltaRewards
    builder
      .addCase(fetchDepositDeltaRewards.pending, (state) => {
        state.depositDeltaStatus = "loading";
        state.depositDeltaError = null;
        state.isDepositDeltaInProgress = true;
      })
      .addCase(fetchDepositDeltaRewards.fulfilled, (state, action: PayloadAction<DepositDeltaRewardsResponse>) => {
        state.depositDeltaStatus = "succeeded";
        state.depositDeltaData = action.payload;
        state.isDepositDeltaInProgress = false;
      })
      .addCase(fetchDepositDeltaRewards.rejected, (state, action) => {
        state.depositDeltaStatus = "failed";
        state.depositDeltaError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to fetch deposit delta rewards";
        state.isDepositDeltaInProgress = false;
      });
  },
});

export const {
  resetFetchAssignedStatus,
  setAssignedRewardBoxes,
  clearAssignedRewardBoxes,
  setSelectedRewardBox,
  resetAssignRewardStatus,
  resetDepositDeltaStatus,
} = rewardSlice.actions;

export default rewardSlice.reducer;

